/**
 * ═══════════════════════════════════════════════════════════════════
 *  NOOSHIN  —  BILINGUAL STRING DEFINITIONS
 *  Global (English) and Persian (فارسی) versions of every UI string.
 *
 *  Usage:
 *    import { useCoreT } from '../context/CoreLangContext';
 *    const T = useCoreT();
 *    <button>{T.save}</button>
 *
 *  coreMode  'global'  →  English  (LTR)
 *  coreMode  'persian' →  Persian  (RTL, font: Vazirmatn)
 * ═══════════════════════════════════════════════════════════════════
 */

const STRINGS = {

  /* ════════════════════════════════════════
     GLOBAL  (English — default)
  ════════════════════════════════════════ */
  global: {
    dir: 'ltr',
    lang: 'en',
    fontFamily: "'DM Sans', sans-serif",
    titleFont: "'Playfair Display', serif",

    /* ── Common ── */
    save:           'Save',
    saveChanges:    'Save Changes',
    cancel:         'Cancel',
    close:          'Close',
    edit:           'Edit',
    delete:         'Delete',
    confirm:        'Confirm',
    yes:            'Yes',
    no:             'No',
    loading:        'Loading…',
    saving:         'Saving…',
    error:          'Error',
    success:        'Success',
    search:         'Search',
    searchDots:     'Search…',
    refresh:        'Refresh',
    add:            'Add',
    remove:         'Remove',
    back:           'Back to Site',
    next:           'Next',
    previous:       'Previous',
    logout:         'Logout',
    login:          'Login',
    signIn:         'Sign In',
    signingIn:      'Signing in…',
    register:       'Register',
    email:          'Email',
    username:       'Username',
    identifier:     'Username or Email',
    password:       'Password',
    newPassword:    'New Password',
    name:           'Name',
    phone:          'Phone',
    address:        'Address',
    active:         'Active',
    inactive:       'Inactive',
    enabled:        'Enabled',
    disabled:       'Disabled',
    all:            'All',
    none:           'None',
    status:         'Status',
    date:           'Date',
    time:           'Time',
    total:          'Total',
    subtotal:       'Subtotal',
    price:          'Price',
    quantity:       'Quantity',
    noData:         'No data available.',
    notFound:       'Not found',
    required:       'required',
    optional:       'optional',
    actions:        'Actions',
    details:        'Details',
    note:           'Note',
    description:    'Description',
    submit:         'Submit',
    update:         'Update',
    create:         'Create',
    view:           'View',
    export:         'Export',
    import:         'Import',
    upload:         'Upload',
    download:       'Download',
    filter:         'Filter',
    sort:           'Sort',
    page:           'Page',
    of:             'of',
    results:        'results',
    retry:          'Retry',
    ok:             'OK',
    reset:          'Reset',
    clear:          'Clear',
    apply:          'Apply',
    type:           'Type',
    more:           'More',
    less:           'Less',
    today:          'Today',
    yesterday:      'Yesterday',
    thisWeek:       'This week',
    thisMonth:      'This month',

    /* ── Platform Admin Sidebar ── */
    platformName:        'Nooshin Platform',
    rootAdmin:           '👑 Root Admin',
    platformAdmin:       '🛡️ Admin',
    overview:            'Overview',
    restaurants:         'Restaurants',
    businessOwners:      'Business Owners',
    platformAdmins:      'Platform Admins',
    settings:            'Settings',
    analytics:           'Analytics',
    saved:               'Saved',
    visitors:            'Visitors',
    visitorsDesc:        'Real traffic to the platform and every tenant, with bots separated from real people.',
    seo:                 'SEO & Google',
    seoDesc:             'Connect Google Analytics, Tag Manager and Search Console, and reach the SEO files — no code needed.',
    seoGa:               'Google Analytics ID (GA4)',
    seoGaHint:           'From Google Analytics → Admin → Data Streams. Tracks all pages automatically.',
    seoGtm:              'Google Tag Manager ID',
    seoGsc:              'Google Search Console verification',
    seoGscHint:          'The content value of the meta tag Search Console gives you (HTML-tag method).',
    seoBing:             'Bing verification',
    seoFiles:            'Crawler files (robots.txt & llms.txt)',
    seoFilesHint:        'See and edit exactly what search engines and AI assistants read. Leave the auto-generated version, or type your own and Save to override it. Reset returns to auto.',
    seoFileAuto:         'Auto-generated',
    seoFileCustom:       'Custom override',
    seoFileOpenLive:     'Open live',
    seoFileReset:        'Reset to auto',
    confirmClearLangTrans:  'Clear all translations for this language?',
    confirmClearAdminTrans: 'Clear all admin panel translations?',
    tTranslating:        'Translating…',
    tAutoAllTo:          'Auto-Translate All to',
    tClear:              'Clear',
    tClearAll:           'Clear All',
    tSavePlatformTrans:  'Save Platform Translations',
    tResetEnglishDefaults: 'Reset All to English Defaults',
    tSaveAllLangs:       'Translate All Languages & Save',
    tEnableOneLang:      'Enable at least one language besides the default first',
    tTranslatedSaved:    'Translated & saved ✓',
    tPlatMethodNote:     'Auto-Translate All to fills the selected language. Translate All Languages & Save translates every enabled language at once and saves immediately. Translation auto-detects the source and uses the AI model plus free providers; review before publishing.',
    adminRole:           'Role',
    roleStandard:        'Standard admin',
    roleSeo:             'SEO admin (full access)',
    roleSeoHint:         'SEO admins get full access to every tool and setting — only managing other admin accounts stays root-only.',
    language:            'Language',
    translations:        'Translations',
    adminLanguage:       'Admin Language',
    coreSettings:        'Core Settings',

    /* ── Platform Overview ── */
    quickActions:        'Quick Actions',
    manageRestaurants:   '🍽️ Manage Restaurants',
    manageOwners:        '👔 Manage Business Owners',
    manageAdmins:        '🛡️ Manage Platform Admins',
    languageSettings:    '🌐 Language Settings',
    totalRestaurants:    'Restaurants',
    totalOwners:         'Business Owners',
    totalCustomers:      'Customers',
    totalPlatformAdmins: 'Platform Admins',

    /* ── Platform Restaurants ── */
    restaurantCount:     (n) => `${n} restaurants`,
    noRestaurants:       'No restaurants yet.',
    editRestaurant:      'Edit Restaurant',
    deleteRestaurant:    'Delete Restaurant',
    confirmDeleteRest:   (name) => `Delete "${name}"? This cannot be undone.`,
    owner:               'Owner',

    /* ── Platform Owners ── */
    ownerCount:          (n) => `${n} business owners`,
    noOwners:            'No business owners yet.',
    suspended:           '✗ Suspended',
    plan:                'Plan',

    /* ── Platform Admins ── */
    newAdmin:            '+ New Admin',
    noAdmins:            'No platform admins found.',
    createAdmin:         'Create Platform Admin',
    editAdmin:           'Edit Platform Admin',
    permissions:         'Permissions',
    lastLogin:           'Last login',
    nameRequired:        'Name, email and password are required for new admins.',
    leaveBlankPassword:  '(leave blank to keep)',
    createAdminBtn:      'Create Admin',

    /* ── Platform Settings ── */
    platformSettings:    'Platform Settings',
    platformNameLabel:   'Platform Name',
    platformTagline:     'Platform Tagline',
    platformFavicon:     'Favicon (browser tab icon)',
    publicRegistration:  'Public Registration',
    allowSelfRegister:   'Allow new restaurants to self-register',
    emailAuth:           'Email Login & Signup',
    emailAuthHint:       'When off, customers and business owners use phone/OTP only (admin username login is unaffected)',
    aiBlog:              'AI SEO Blog',
    aiBlogHint:          'Auto-publish a daily multilingual article for the platform to boost search ranking',
    aiEndpoints:         'AI / LLM Endpoints',
    aiEndpointsHint:     'Point the platform at your own local model and cloud provider. Leave a field empty to use the built-in default.',
    aiLocalSection:      'Local model (LM Studio / Ollama)',
    aiLocalUrl:          'Local LLM URL',
    aiLocalUrlHint:      'OpenAI-compatible base URL, e.g. http://192.168.1.50:1234/v1 — leave empty to disable local AI (cloud only)',
    aiLocalModel:        'Local model name',
    aiLocalFirst:        'Prefer local first',
    aiLocalFirstHint:    'Use the local model for text first, falling back to the cloud',
    aiCloudSection:      'Cloud provider',
    aiCloudBase:         'Cloud API base URL',
    aiCloudKey:          'Cloud API key',
    aiCloudModel:        'Cloud model name',
    aiTestConnection:    'Test connection',
    aiTesting:           'Testing…',
    aiTestLocalOk:       'Local OK',
    aiTestLocalFail:     'Local failed',
    aiTestLocalOff:      'Local disabled',
    aiTestCloudOk:       'Cloud OK',
    aiTestCloudFail:     'Cloud failed',
    generatePostNow:     'Generate a post now',
    generating:          'Generating…',
    postGenerated:       'New blog post published',
    postGenerateFailed:  'Could not generate post',
    growthHeading:       'Growth — Trials & Referrals',
    trialDays:           'Free trial length (days)',
    trialDaysHint:       'How many days new sign-ups get full access before they must subscribe',
    freeMenu:            'Free menu after trial',
    freeMenuHint:        'When on, expired trials keep a basic free menu instead of being locked',
    referralProgram:     'Referral program',
    referralProgramHint: 'Give each owner a referral link; reward both sides when a friend joins',
    referralRewardDays:  'Referrer reward (days)',
    refereeRewardDays:   'New-signup bonus (days)',
    maintenanceMode:     'Maintenance Mode',
    maintenanceModeHint: 'Show maintenance page to visitors',
    localizeImages:      'Localize Images',
    localizeImagesBtn:   'Localize all images',
    localizeImagesHint:  'Download every remote (Iran-blocked) image through the proxy pool and re-serve it from our own server. Safe to re-run.',
    localizeRunning:     'Localizing…',
    localizeStarted:     'Image localization started',
    localizeDone:        'Done',
    savePlatformSettings:'Save Platform Settings',
    on:                  '🟢 ON',
    off:                 '⚪ OFF',

    /* ── Core Settings ── */
    coreSettingsTitle:       'Core Settings',
    coreSettingsDesc:        'Choose the primary language and direction for this entire platform installation. This affects every panel, page, and label system-wide.',
    coreModeLabel:           'Platform Core Mode',
    globalModeTitle:         'Global Mode',
    globalModeDesc:          'The platform runs in English. Suitable for international deployments worldwide.',
    persianModeTitle:        'Persian Mode (حالت فارسی)',
    persianModeDesc:         'Everything in the platform — every label, button and message — becomes Persian (Farsi). Suitable for Iran-specific servers.',
    currentMode:             'Current mode',
    saveCoreSetting:         'Save Core Settings',
    coreSavedMsg:            'Core settings saved ✓',
    warningReloadTitle:      '⚠️ Mode Change',
    warningReloadDesc:       'Changing the core mode will reload the page to apply the new language across all panels.',
    globalBadge:             '🌍 Global',
    persianBadge:            '🇮🇷 Persian',

    /* ── Analytics ── */
    analyticsTitle:       'Platform Analytics & Insights',
    analyticsDesc:        'Real-time data from all restaurants • AI analyst • Full audit trail',
    restaurantAnalytics:  'Analytics & AI Insights',
    restaurantAnalyticsSub:'Real-time data • AI analyst • Full audit trail',

    /* ── Restaurant Admin Sidebar ── */
    admPanel:          'Admin Panel',
    orders:            'Orders',
    products:          'Products',
    customers:         'Customers',
    categories:        'Categories',
    restaurantSettings:'Restaurant Settings',
    cashier:           'Cashier',
    kitchen:           'Kitchen',
    branches:          'Branches',
    events:            'Events',
    menuImport:        'Menu Import',
    subAdmins:         'Staff',
    analyticsAI:       'Analytics & AI',

    /* ── Orders ── */
    orderNumber:       'Order #',
    customerName:      'Customer',
    itemCount:         'Items',
    orderType:         'Type',
    paymentMethod:     'Payment',
    paymentStatus:     'Pay Status',
    tableNumber:       'Table',
    deliveryAddress:   'Delivery Address',
    orderNote:         'Note',
    orderStatus:       'Status',
    pending:           'Pending',
    confirmed:         'Confirmed',
    preparing:         'Preparing',
    ready:             'Ready',
    delivered:         'Delivered',
    cancelled:         'Cancelled',
    paid:              'Paid',
    unpaid:            'Unpaid',
    cash:              'Cash',
    card:              'Card',
    online:            'Online',
    dineIn:            'Dine-in',
    takeaway:          'Takeaway',
    deliveryType:      'Delivery',
    serviceFee:        'Service Fee',
    totalAmount:       'Total',
    printReceipt:      'Print Receipt',
    printKitchen:      'Print Kitchen',
    filterOrders:      'Filter orders…',
    allStatuses:       'All Statuses',
    allTypes:          'All Types',
    allPayments:       'All Payments',
    todayOrders:       "Today's Orders",
    liveOrders:        'Live Orders',
    noOrders:          'No orders found.',

    /* ── Products / Menu ── */
    productName:       'Product Name',
    category:          'Category',
    available:         'Available',
    unavailable:       'Unavailable',
    image:             'Image',
    tags:              'Tags',
    addProduct:        'Add Product',
    editProduct:       'Edit Product',
    deleteProduct:     'Delete Product',
    confirmDeleteProd: 'Delete this product?',
    sortOrder:         'Sort Order',
    subCategory:       'Sub-category',
    bulkPriceAdj:      'Bulk Price Adjustment',
    noProducts:        'No products yet.',
    addFirstProduct:   '+ Add your first product',
    availableToggle:   'Toggle availability',

    /* ── Categories ── */
    categoryName:      'Category Name',
    categorySlug:      'Slug',
    categoryIcon:      'Icon',
    addCategory:       'Add Category',
    editCategory:      'Edit Category',
    deleteCategory:    'Delete Category',
    confirmDeleteCat:  'Delete this category and ALL its products?',
    noCategories:      'No categories yet.',
    subCategories:     'Sub-categories',

    /* ── Customers ── */
    customerSince:     'Customer since',
    totalSpent:        'Total spent',
    lastOrder:         'Last order',
    noCustomers:       'No customers found.',
    customerDetails:   'Customer Details',
    orderHistory:      'Order History',
    totalOrders:       'Total Orders',

    /* ── Restaurant Settings ── */
    basicInfo:         'Basic Info',
    contact:           'Contact & Location',
    appearance:        'Appearance',
    hours:             'Opening Hours',
    primaryColor:      'Primary Color',
    logo:              'Logo',
    coverImage:        'Cover Image',
    cuisine:           'Cuisine',
    tagline:           'Tagline',
    currency:          'Currency',
    saveSettings:      'Save Restaurant Settings',

    /* ── Sub-admins / Staff ── */
    staffEmail:        'Staff Email',
    staffName:         'Staff Name',
    staffRole:         'Role',
    staffPassword:     'Password',
    addStaff:          'Add Staff',
    editStaff:         'Edit Staff',
    deleteStaff:       'Delete Staff',
    noStaff:           'No staff members yet.',
    roleRoot:          'Root Admin',
    roleFull:          'Manager',
    roleCashier:       'Cashier',
    roleKitchen:       'Kitchen',
    roleLimited:       'Limited',

    /* ── Customer-facing: Menu ── */
    searchMenu:        'Search menu…',
    allCats:           'All',
    noItemsFound:      'No items found.',
    addToCart:         'Add to Cart',
    added:             'Added!',
    outOfStock:        'Out of Stock',
    viewCart:          'View Cart',
    cartCount:         (n) => `${n} item${n===1?'':'s'}`,
    openMenu:          'Open Menu',

    /* ── Customer-facing: Cart ── */
    cart:              'Your Cart',
    emptyCart:         'Your cart is empty',
    emptyCartSub:      'Browse the menu and add items.',
    goToMenu:          '← Back to Menu',
    orderSummary:      'Order Summary',
    placeOrder:        'Place Order',
    placingOrder:      'Placing order…',
    proceedToOrder:    'Proceed to Order',
    orderType_label:   'How would you like your order?',
    tableNum:          'Table Number',
    deliveryAddr:      'Delivery Address',
    specialInstructions:'Special Instructions',
    paymentMethod_label:'Payment Method',
    choosePayment:     'Choose payment…',
    total_label:       'Total',
    items_label:       'items',
    removeItem:        'Remove',
    loginToContinue:   'Login to place your order',

    /* ── Customer-facing: Auth ── */
    welcomeBack:       'Welcome Back',
    createAccount:     'Create Account',
    loginToContinue2:  'Login to continue',
    dontHaveAccount:   "Don't have an account?",
    alreadyHaveAccount:'Already have an account?',
    registerHere:      'Register here',
    loginHere:         'Login here',
    fullName:          'Full Name',
    confirmPassword:   'Confirm Password',
    forgotPassword:    'Forgot password?',
    orContinueAs:      'or continue as guest',
    signInBtn:         'Sign In',
    registerBtn:       'Create Account',
    loggingIn:         'Logging in…',
    registering:       'Creating account…',

    /* ── Customer Profile ── */
    myProfile:         'My Profile',
    myOrders:          'My Orders',
    editProfile:       'Edit Profile',
    saveProfile:       'Save Profile',
    profileSaved:      'Profile saved ✓',
    noOrdersYet:       'No orders yet.',
    orderDate:         'Date',
    orderItems:        'Items',
    orderTotal:        'Total',

    /* ── Business Owner ── */
    businessDashboard: 'Business Dashboard',
    myRestaurants:     'My Restaurants',
    manageRestaurant:  'Manage Restaurant',
    openAdminPanel:    'Open Admin Panel',
    businessLogin:     'Business Owner Login',
    businessRegister:  'Create Business Account',
    noRestLinked:      'No restaurants linked to your account.',
    contactSupport:    'Contact support to add restaurants.',

    /* ── Platform Login ── */
    platformLogin:     'Platform Admin Login',
    platformLoginSub:  'Restricted access — authorized personnel only',
    platformSignIn:    'Sign In to Platform',

    /* ── Admin Login ── */
    adminLogin:        'Admin Login',
    backToSite:        '← Back to Site',
    businessOwnerLogin:'Business Owner Login',
    demo:              'Demo',

    /* ── Business Auth / Register ── */
    bizTagline:          'The all-in-one digital menu platform for restaurants & cafes',
    bizFeatureMenu:      'Beautiful subdomain menu',
    bizFeatureOrders:    'Real-time order management',
    bizFeatureProducts:  'Product & category management',
    bizFeatureInsights:  'Customer insights & analytics',
    bizFeatureTenant:    'Multi-tenant — fully isolated',
    bizFeatureMobile:    'Mobile-first experience',
    bizRegisterBusiness: 'Register Business',
    bizSignInDashboard:  'Sign In to Dashboard',
    bizRegistering:      'Registering…',
    bizRegisterMyBiz:    'Register My Business 🚀',
    bizBusinessEmail:    'Business Email',
    bizRestaurantName:   'Restaurant Name',
    bizOwnerName:        'Owner Name',
    bizMenuSubdomain:    'Menu Subdomain',
    bizUsedForSms:       'used for SMS login',
    bizCreateStrongPass: 'Create a strong password',
    bizTerms:            'By registering you agree to our Terms of Service and Privacy Policy.',
    bizBackToHome:       '← Back to Home',
    bizPhoneMethod:      '📱 Phone',
    bizEmailMethod:      '✉️ Email',
    bizEmailReq:         'Email and password required.',
    bizAllFieldsReq:     'Please fill in all required fields.',

    /* ── Phone-OTP widget (passed into PhoneOtpForm as labels) ── */
    otpPhoneLabel:       'Mobile number',
    otpSendBtn:          'Send code',
    otpCodeLabel:        'Verification code',
    otpVerifyBtn:        'Confirm & continue',
    otpNameLabel:        'Your name (optional)',
    otpNamePh:           'e.g. Sara',
    otpChangePhone:      'Change number',
    otpResend:           'Resend code',
    otpSentTo:           'Code sent to',

    /* ── Platform language admin + shared form labels ── */
    languageMode:          'Language Mode',
    defaultLanguage:       'Default Language',
    enabledLanguages:      'Enabled Languages',
    restaurantName:        'Restaurant Name',
    platformUiLangMode:    'Platform UI Language Mode',
    defaultPlatformLang:   'Default Platform Language',
    enabledLangsUiSwitcher:'Enabled Languages for UI Switcher',
    enabledLangsHint:      'These languages will appear in the flag dropdown on the platform homepage.',
    switcherPreview:       'Switcher Preview',
    saveLanguageSettings:  'Save Language Settings',
    dashboardLanguage:     'Dashboard Language',
    dashboardLanguageHint: 'The language you and other admins see this dashboard and the login pages in. Independent of the public site language below.',
    publicSiteLanguage:    'Public Site Language',
    publicSiteLanguageHint:'The language(s) your visitors see on the public homepage and shell.',
    polishTitle:           'Translation Quality (AI Polish)',
    polishHint:            'Re-checks an auto-translated language and rewrites awkward or literal wording. Uses your local AI first, then the cloud.',
    polishBtn:             '✨ Polish with AI',
    polishing:             'Polishing…',
    polishDoneMsg:         'Polished — improved {n} strings ✓',
    polishNoneMsg:         'Already good — no changes needed ✓',
    platformHomepageTranslations: 'Platform Homepage Translations',
    translateInto:         'Translate Into',
    adminPanelLanguage:    'Admin Panel Language',
    adminPanelLangDesc:    'Translates the labels, tabs and buttons inside this dashboard only. Your customers never see these strings. English is always the fallback.',
    chooseLanguage:        'Choose Language',

    /* ── Platform toast messages ── */
    tAdminCreated:     'Admin created ✓',
    tAdminDeleted:     'Admin deleted',
    tAdminUpdated:     'Admin updated ✓',
    tAdminTranslated:  'Admin panel translated ✓',
    tDashLangUpdated:  'Dashboard language updated ✓',
    tPlanUpdated:      'Plan updated',
    tSettingsSaved:    'Platform settings saved ✓',
    tTransDone:        'Platform translations done ✓',
    tResetDefaults:    'Reset to English defaults ✓',
    tRestDeleted:      'Restaurant deleted',
    tRestUpdated:      'Restaurant updated ✓',
    tLangFailed:       'Could not change language',
    tRestDeactivated:  'Restaurant deactivated',
    tRestActivated:    'Restaurant activated',
    tOwnerDeactivated: 'Owner deactivated',
    tOwnerActivated:   'Owner activated',

    /* ── System mode ── */
    systemMode:        'System Mode',
    accentColor:       'Accent Color',
    baseMode:          'Base Mode',
    dark:              'Dark',
    light:             'Light',
    production:        'Production',
    development:       'Development',
    systemModeHint:    'Production sends real SMS via the provider. Development logs OTP codes to the server console and shows them on screen (no SMS sent, no credit used).',
    resetPassword:     'Reset Password',
    resetPasswordSub:  'Enter your account phone number and a new password. We’ll send a code to confirm.',
    forgotPassword:    'Forgot password?',

    /* ── Analytics Panel ── */
    analyticsPeriod:   'Period',
    days7:             '7 days',
    days14:            '14 days',
    days30:            '30 days',
    days90:            '90 days',
    revenue:           'Revenue',
    ordersCount:       'Orders',
    avgOrder:          'Avg Order',
    activeRests:       'Active Restaurants',
    todayRevenue:      "Today's Revenue",
    todayOrders2:      "Today's Orders",
    topProducts:       'Top Products',
    topRestaurants:    'Top Restaurants',
    recentActivity:    'Recent Activity',
    auditLogs:         'Audit Logs',
    aiAnalyst:         'AI Analyst',
    exportCSV:         '⬇ Export CSV',
    noLogs:            'No logs found.',
    askAI:             'Ask about your data…',
    send:              'Send',
    aiThinking:        'Analysing your data…',
    aiSuggestions:     ['What are my top selling items this month?', 'How does my revenue compare to last month?', 'When are my peak hours?', 'Which items should I promote?'],
    tabOverview:       'Overview',
    tabProducts:       'Products',
    tabActivity:       'Activity',
    tabAI:             'AI Analyst',
    tabAudit:          'Audit Logs',

    /* ── Notifications ── */
    savedOK:           'Saved ✓',
    deletedOK:         'Deleted',
    updatedOK:         'Updated ✓',
    createdOK:         'Created ✓',
    errorOccurred:     'An error occurred',
    confirmDelete:     'Are you sure you want to delete this?',

    /* ── Errors & Misc ── */
    serverError:       'Could not reach the server',
    serverErrorDesc:   'Please check your connection and try again.',
    pageNotFound:      'Page Not Found',
    pageNotFoundDesc:  'The page you are looking for does not exist.',
    maintenance:       'Under Maintenance',
    maintenanceDesc:   'We are currently performing maintenance. Please check back soon.',
  },

  /* ════════════════════════════════════════
     PERSIAN  (فارسی — حالت ایران)
  ════════════════════════════════════════ */
  persian: {
    dir: 'rtl',
    lang: 'fa',
    fontFamily: "'Vazirmatn', 'Tahoma', sans-serif",
    titleFont: "'Vazirmatn', 'Tahoma', sans-serif",

    /* ── مشترک ── */
    save:           'ذخیره',
    saveChanges:    'ذخیره تغییرات',
    cancel:         'لغو',
    close:          'بستن',
    edit:           'ویرایش',
    delete:         'حذف',
    confirm:        'تأیید',
    yes:            'بله',
    no:             'خیر',
    loading:        'در حال بارگذاری...',
    saving:         'در حال ذخیره...',
    error:          'خطا',
    success:        'موفق',
    search:         'جستجو',
    searchDots:     'جستجو...',
    refresh:        'بروزرسانی',
    add:            'افزودن',
    remove:         'حذف',
    back:           'بازگشت به سایت',
    next:           'بعدی',
    previous:       'قبلی',
    logout:         'خروج',
    login:          'ورود',
    signIn:         'ورود به حساب',
    signingIn:      'در حال ورود...',
    register:       'ثبت‌نام',
    email:          'ایمیل',
    username:       'نام کاربری',
    identifier:     'نام کاربری یا ایمیل',
    password:       'رمز عبور',
    newPassword:    'رمز عبور جدید',
    name:           'نام',
    phone:          'شماره تلفن',
    address:        'آدرس',
    active:         'فعال',
    inactive:       'غیرفعال',
    enabled:        'فعال‌شده',
    disabled:       'غیرفعال‌شده',
    all:            'همه',
    none:           'هیچ',
    status:         'وضعیت',
    date:           'تاریخ',
    time:           'زمان',
    total:          'مجموع',
    subtotal:       'جمع جزء',
    price:          'قیمت',
    quantity:       'تعداد',
    noData:         'داده‌ای موجود نیست.',
    notFound:       'پیدا نشد',
    required:       'الزامی',
    optional:       'اختیاری',
    actions:        'عملیات',
    details:        'جزئیات',
    note:           'یادداشت',
    description:    'توضیحات',
    submit:         'ارسال',
    update:         'بروزرسانی',
    create:         'ایجاد',
    view:           'مشاهده',
    export:         'خروجی',
    import:         'ورودی',
    upload:         'بارگذاری',
    download:       'دانلود',
    filter:         'فیلتر',
    sort:           'مرتب‌سازی',
    page:           'صفحه',
    of:             'از',
    results:        'نتیجه',
    retry:          'تلاش مجدد',
    ok:             'باشه',
    reset:          'بازنشانی',
    clear:          'پاک کردن',
    apply:          'اعمال',
    type:           'نوع',
    more:           'بیشتر',
    less:           'کمتر',
    today:          'امروز',
    yesterday:      'دیروز',
    thisWeek:       'این هفته',
    thisMonth:      'این ماه',

    /* ── نوار کناری پلتفرم ── */
    platformName:        'پلتفرم نوشین',
    rootAdmin:           '👑 مدیر ارشد',
    platformAdmin:       '🛡️ مدیر',
    overview:            'نمای کلی',
    restaurants:         'رستوران‌ها',
    businessOwners:      'صاحبان کسب‌وکار',
    platformAdmins:      'مدیران پلتفرم',
    settings:            'تنظیمات',
    analytics:           'آمار و تحلیل',
    saved:               'ذخیره شد',
    visitors:            'بازدیدکنندگان',
    visitorsDesc:        'ترافیک واقعی پلتفرم و هر مستأجر، با تفکیک ربات‌ها از افراد واقعی.',
    seo:                 'سئو و گوگل',
    seoDesc:             'گوگل آنالیتیکس، تگ منیجر و سرچ کنسول را وصل کنید و به فایل‌های سئو دسترسی داشته باشید — بدون نیاز به کد.',
    seoGa:               'شناسه گوگل آنالیتیکس (GA4)',
    seoGaHint:           'از مسیر Google Analytics ← Admin ← Data Streams. همه صفحات را خودکار ردیابی می‌کند.',
    seoGtm:              'شناسه گوگل تگ منیجر',
    seoGsc:              'تأیید مالکیت گوگل سرچ کنسول',
    seoGscHint:          'مقدار content متایی که سرچ کنسول به شما می‌دهد (روش HTML tag).',
    seoBing:             'تأیید مالکیت بینگ',
    seoFiles:            'فایل‌های خزنده (robots.txt و llms.txt)',
    seoFilesHint:        'دقیقاً همان چیزی را که موتورهای جست‌وجو و دستیارهای هوش مصنوعی می‌خوانند ببینید و ویرایش کنید. نسخهٔ خودکار را نگه دارید یا متن دلخواه خود را بنویسید و با ذخیره جایگزین کنید. بازنشانی به حالت خودکار برمی‌گرداند.',
    seoFileAuto:         'تولید خودکار',
    seoFileCustom:       'جایگزین سفارشی',
    seoFileOpenLive:     'مشاهده زنده',
    seoFileReset:        'بازنشانی به خودکار',
    confirmClearLangTrans:  'همهٔ ترجمه‌های این زبان پاک شوند؟',
    confirmClearAdminTrans: 'همهٔ ترجمه‌های پنل مدیریت پاک شوند؟',
    tTranslating:        'در حال ترجمه…',
    tAutoAllTo:          'ترجمهٔ خودکار همه به',
    tClear:              'پاک کردن',
    tClearAll:           'پاک کردن همه',
    tSavePlatformTrans:  'ذخیرهٔ ترجمه‌های پلتفرم',
    tResetEnglishDefaults: 'بازنشانی همه به پیش‌فرض‌های انگلیسی',
    tSaveAllLangs:       'ترجمهٔ همهٔ زبان‌ها و ذخیره',
    tEnableOneLang:      'ابتدا حداقل یک زبان غیر از زبان پیش‌فرض را فعال کنید',
    tTranslatedSaved:    'ترجمه و ذخیره شد ✓',
    tPlatMethodNote:     '«ترجمهٔ خودکار همه به» زبان انتخاب‌شده را پر می‌کند. «ترجمهٔ همهٔ زبان‌ها و ذخیره» همهٔ زبان‌های فعال را یک‌جا ترجمه و بلافاصله ذخیره می‌کند. ترجمه زبان مبدأ را خودکار تشخیص می‌دهد و از مدل هوش مصنوعی به‌همراه ارائه‌دهندگان رایگان استفاده می‌کند؛ پیش از انتشار بازبینی کنید.',
    adminRole:           'نقش',
    roleStandard:        'مدیر عادی',
    roleSeo:             'مدیر سئو (دسترسی کامل)',
    roleSeoHint:         'مدیران سئو به همهٔ ابزارها و تنظیمات دسترسی کامل دارند — فقط مدیریت حساب سایر مدیران مخصوص روت می‌ماند.',
    language:            'زبان',
    translations:        'ترجمه‌ها',
    adminLanguage:       'زبان پنل مدیریت',
    coreSettings:        'تنظیمات اصلی',

    /* ── نمای کلی پلتفرم ── */
    quickActions:        'دسترسی سریع',
    manageRestaurants:   '🍽️ مدیریت رستوران‌ها',
    manageOwners:        '👔 مدیریت صاحبان کسب‌وکار',
    manageAdmins:        '🛡️ مدیریت مدیران پلتفرم',
    languageSettings:    '🌐 تنظیمات زبان',
    totalRestaurants:    'رستوران‌ها',
    totalOwners:         'صاحبان کسب‌وکار',
    totalCustomers:      'مشتریان',
    totalPlatformAdmins: 'مدیران پلتفرم',

    /* ── رستوران‌های پلتفرم ── */
    restaurantCount:     (n) => `${n} رستوران`,
    noRestaurants:       'هنوز رستورانی ثبت نشده است.',
    editRestaurant:      'ویرایش رستوران',
    deleteRestaurant:    'حذف رستوران',
    confirmDeleteRest:   (name) => `حذف "${name}"؟ این عملیات قابل بازگشت نیست.`,
    owner:               'مالک',

    /* ── صاحبان کسب‌وکار ── */
    ownerCount:          (n) => `${n} صاحب کسب‌وکار`,
    noOwners:            'هنوز صاحب کسب‌وکاری ثبت نشده است.',
    suspended:           '✗ معلق',
    plan:                'پلن',

    /* ── مدیران پلتفرم ── */
    newAdmin:            '+ مدیر جدید',
    noAdmins:            'هیچ مدیر پلتفرمی پیدا نشد.',
    createAdmin:         'ایجاد مدیر پلتفرم',
    editAdmin:           'ویرایش مدیر پلتفرم',
    permissions:         'دسترسی‌ها',
    lastLogin:           'آخرین ورود',
    nameRequired:        'نام، ایمیل و رمز عبور برای مدیران جدید الزامی است.',
    leaveBlankPassword:  '(برای حفظ رمز قبلی خالی بگذارید)',
    createAdminBtn:      'ایجاد مدیر',

    /* ── تنظیمات پلتفرم ── */
    platformSettings:    'تنظیمات پلتفرم',
    platformNameLabel:   'نام پلتفرم',
    platformTagline:     'شعار پلتفرم',
    platformFavicon:     'فاوآیکن (آیکن تب مرورگر)',
    publicRegistration:  'ثبت‌نام عمومی',
    allowSelfRegister:   'اجازه دادن به رستوران‌ها برای ثبت‌نام مستقل',
    emailAuth:           'ورود و ثبت‌نام با ایمیل',
    emailAuthHint:       'وقتی خاموش است، مشتریان و صاحبان کسب‌وکار فقط با شماره موبایل/کد وارد می‌شوند (ورود مدیر با نام کاربری تأثیری نمی‌گیرد)',
    aiBlog:              'وبلاگ سئوی هوش مصنوعی',
    aiBlogHint:          'انتشار خودکار یک مقالهٔ چندزبانهٔ روزانه برای پلتفرم جهت بهبود رتبهٔ جستجو',
    aiEndpoints:         'سرویس‌های هوش مصنوعی / LLM',
    aiEndpointsHint:     'پلتفرم را به مدل محلی و سرویس ابری خودتان متصل کنید. برای استفاده از مقدار پیش‌فرض، فیلد را خالی بگذارید.',
    aiLocalSection:      'مدل محلی (LM Studio / Ollama)',
    aiLocalUrl:          'آدرس مدل محلی (LLM)',
    aiLocalUrlHint:      'آدرس پایهٔ سازگار با OpenAI، مثلاً http://192.168.1.50:1234/v1 — برای غیرفعال‌کردن مدل محلی خالی بگذارید (فقط ابری)',
    aiLocalModel:        'نام مدل محلی',
    aiLocalFirst:        'اولویت با مدل محلی',
    aiLocalFirstHint:    'ابتدا برای متن از مدل محلی استفاده کن و در صورت خطا به ابری بازگرد',
    aiCloudSection:      'سرویس ابری',
    aiCloudBase:         'آدرس پایهٔ API ابری',
    aiCloudKey:          'کلید API ابری',
    aiCloudModel:        'نام مدل ابری',
    aiTestConnection:    'تست اتصال',
    aiTesting:           'در حال تست…',
    aiTestLocalOk:       'محلی سالم',
    aiTestLocalFail:     'خطای محلی',
    aiTestLocalOff:      'محلی غیرفعال',
    aiTestCloudOk:       'ابری سالم',
    aiTestCloudFail:     'خطای ابری',
    generatePostNow:     'همین حالا یک مقاله بساز',
    generating:          'در حال ساخت…',
    postGenerated:       'مقالهٔ جدید منتشر شد',
    postGenerateFailed:  'ساخت مقاله ناموفق بود',
    growthHeading:       'رشد — دورهٔ آزمایشی و معرفی دوستان',
    trialDays:           'مدت دورهٔ آزمایشی رایگان (روز)',
    trialDaysHint:       'تعداد روزهایی که کاربران تازه‌وارد پیش از اشتراک، دسترسی کامل دارند',
    freeMenu:            'منوی رایگان پس از دورهٔ آزمایشی',
    freeMenuHint:        'وقتی روشن است، دورهٔ آزمایشیِ پایان‌یافته به‌جای قفل‌شدن، یک منوی پایهٔ رایگان نگه می‌دارد',
    referralProgram:     'برنامهٔ معرفی دوستان',
    referralProgramHint: 'به هر صاحب کسب‌وکار یک لینک معرفی بده؛ هنگام عضویت دوست، به هر دو طرف پاداش بده',
    referralRewardDays:  'پاداش معرف (روز)',
    refereeRewardDays:   'پاداش کاربر جدید (روز)',
    maintenanceMode:     'حالت تعمیر و نگهداری',
    maintenanceModeHint: 'نمایش صفحه تعمیر برای بازدیدکنندگان',
    localizeImages:      'بومی‌سازی تصاویر',
    localizeImagesBtn:   'بومی‌سازی همه تصاویر',
    localizeImagesHint:  'همه تصاویر راه‌دور (فیلترشده در ایران) از طریق مجموعه پروکسی دانلود شده و از سرور خودمان سرو می‌شوند. اجرای دوباره بی‌خطر است.',
    localizeRunning:     'در حال بومی‌سازی…',
    localizeStarted:     'بومی‌سازی تصاویر آغاز شد',
    localizeDone:        'انجام شد',
    savePlatformSettings:'ذخیره تنظیمات پلتفرم',
    on:                  '🟢 روشن',
    off:                 '⚪ خاموش',

    /* ── تنظیمات اصلی ── */
    coreSettingsTitle:       'تنظیمات اصلی',
    coreSettingsDesc:        'زبان و جهت اصلی این نصب پلتفرم را انتخاب کنید. این تنظیم روی تمام پنل‌ها، صفحات و برچسب‌های سیستم تأثیر می‌گذارد.',
    coreModeLabel:           'حالت اصلی پلتفرم',
    globalModeTitle:         'حالت جهانی',
    globalModeDesc:          'پلتفرم به زبان انگلیسی اجرا می‌شود. مناسب برای استقرار بین‌المللی در سراسر جهان.',
    persianModeTitle:        'حالت فارسی (Persian Mode)',
    persianModeDesc:         'تمام پلتفرم — هر برچسب، دکمه و پیام — به فارسی تبدیل می‌شود. مناسب برای سرورهای اختصاصی ایران.',
    currentMode:             'حالت فعلی',
    saveCoreSetting:         'ذخیره تنظیمات اصلی',
    coreSavedMsg:            'تنظیمات اصلی ذخیره شد ✓',
    warningReloadTitle:      '⚠️ تغییر حالت',
    warningReloadDesc:       'تغییر حالت اصلی، صفحه را مجدداً بارگذاری می‌کند تا زبان جدید در تمام پنل‌ها اعمال شود.',
    globalBadge:             '🌍 جهانی',
    persianBadge:            '🇮🇷 فارسی',

    /* ── آمار و تحلیل ── */
    analyticsTitle:       'آمار و تحلیل پلتفرم',
    analyticsDesc:        'داده‌های لحظه‌ای از تمام رستوران‌ها • تحلیل‌گر هوش مصنوعی • گزارش کامل رویدادها',
    restaurantAnalytics:  'آمار و تحلیل هوشمند',
    restaurantAnalyticsSub:'داده‌های لحظه‌ای • تحلیل‌گر هوش مصنوعی • گزارش رویدادها',

    /* ── نوار کناری پنل رستوران ── */
    admPanel:          'پنل مدیریت',
    orders:            'سفارش‌ها',
    products:          'محصولات',
    customers:         'مشتریان',
    categories:        'دسته‌بندی‌ها',
    restaurantSettings:'تنظیمات رستوران',
    cashier:           'صندوق',
    kitchen:           'آشپزخانه',
    branches:          'شعب',
    events:            'رویدادها',
    menuImport:        'واردسازی منو',
    subAdmins:         'کارکنان',
    analyticsAI:       'آمار و هوش مصنوعی',

    /* ── سفارش‌ها ── */
    orderNumber:       'شماره سفارش',
    customerName:      'مشتری',
    itemCount:         'اقلام',
    orderType:         'نوع سفارش',
    paymentMethod:     'روش پرداخت',
    paymentStatus:     'وضعیت پرداخت',
    tableNumber:       'شماره میز',
    deliveryAddress:   'آدرس تحویل',
    orderNote:         'یادداشت',
    orderStatus:       'وضعیت سفارش',
    pending:           'در انتظار',
    confirmed:         'تأیید شده',
    preparing:         'در حال آماده‌سازی',
    ready:             'آماده تحویل',
    delivered:         'تحویل داده شده',
    cancelled:         'لغو شده',
    paid:              'پرداخت شده',
    unpaid:            'پرداخت نشده',
    cash:              'نقد',
    card:              'کارت',
    online:            'آنلاین',
    dineIn:            'حضوری',
    takeaway:          'بیرون‌بر',
    deliveryType:      'تحویل درب',
    serviceFee:        'هزینه سرویس',
    totalAmount:       'مبلغ کل',
    printReceipt:      'پرینت رسید',
    printKitchen:      'پرینت آشپزخانه',
    filterOrders:      'جستجوی سفارش‌ها...',
    allStatuses:       'همه وضعیت‌ها',
    allTypes:          'همه انواع',
    allPayments:       'همه روش‌های پرداخت',
    todayOrders:       'سفارش‌های امروز',
    liveOrders:        'سفارش‌های زنده',
    noOrders:          'سفارشی یافت نشد.',

    /* ── محصولات / منو ── */
    productName:       'نام محصول',
    category:          'دسته‌بندی',
    available:         'موجود',
    unavailable:       'ناموجود',
    image:             'تصویر',
    tags:              'برچسب‌ها',
    addProduct:        'افزودن محصول',
    editProduct:       'ویرایش محصول',
    deleteProduct:     'حذف محصول',
    confirmDeleteProd: 'این محصول حذف شود؟',
    sortOrder:         'ترتیب نمایش',
    subCategory:       'زیردسته',
    bulkPriceAdj:      'تنظیم قیمت گروهی',
    noProducts:        'هنوز محصولی اضافه نشده است.',
    addFirstProduct:   '+ افزودن اولین محصول',
    availableToggle:   'تغییر وضعیت موجودی',

    /* ── دسته‌بندی‌ها ── */
    categoryName:      'نام دسته‌بندی',
    categorySlug:      'شناسه',
    categoryIcon:      'آیکون',
    addCategory:       'افزودن دسته‌بندی',
    editCategory:      'ویرایش دسته‌بندی',
    deleteCategory:    'حذف دسته‌بندی',
    confirmDeleteCat:  'این دسته‌بندی و تمام محصولات آن حذف شود؟',
    noCategories:      'هنوز دسته‌بندی اضافه نشده است.',
    subCategories:     'زیردسته‌ها',

    /* ── مشتریان ── */
    customerSince:     'مشتری از',
    totalSpent:        'کل خرید',
    lastOrder:         'آخرین سفارش',
    noCustomers:       'مشتری‌ای پیدا نشد.',
    customerDetails:   'جزئیات مشتری',
    orderHistory:      'تاریخچه سفارش‌ها',
    totalOrders:       'تعداد سفارش‌ها',

    /* ── تنظیمات رستوران ── */
    basicInfo:         'اطلاعات اصلی',
    contact:           'تماس و موقعیت',
    appearance:        'ظاهر',
    hours:             'ساعات کاری',
    primaryColor:      'رنگ اصلی',
    logo:              'لوگو',
    coverImage:        'تصویر کاور',
    cuisine:           'نوع غذا',
    tagline:           'شعار',
    currency:          'ارز',
    saveSettings:      'ذخیره تنظیمات رستوران',

    /* ── کارکنان ── */
    staffEmail:        'ایمیل کارمند',
    staffName:         'نام کارمند',
    staffRole:         'نقش',
    staffPassword:     'رمز عبور',
    addStaff:          'افزودن کارمند',
    editStaff:         'ویرایش کارمند',
    deleteStaff:       'حذف کارمند',
    noStaff:           'هنوز کارمندی اضافه نشده است.',
    roleRoot:          'مدیر ارشد',
    roleFull:          'مدیر',
    roleCashier:       'صندوق‌دار',
    roleKitchen:       'آشپزخانه',
    roleLimited:       'محدود',

    /* ── منوی مشتری ── */
    searchMenu:        'جستجو در منو...',
    allCats:           'همه',
    noItemsFound:      'موردی یافت نشد.',
    addToCart:         'افزودن به سبد',
    added:             'افزوده شد!',
    outOfStock:        'ناموجود',
    viewCart:          'مشاهده سبد',
    cartCount:         (n) => `${n} مورد`,
    openMenu:          'باز کردن منو',

    /* ── سبد خرید ── */
    cart:              'سبد خرید',
    emptyCart:         'سبد خرید شما خالی است',
    emptyCartSub:      'از منو اقلام مورد نظر را انتخاب کنید.',
    goToMenu:          '→ بازگشت به منو',
    orderSummary:      'خلاصه سفارش',
    placeOrder:        'ثبت سفارش',
    placingOrder:      'در حال ثبت سفارش...',
    proceedToOrder:    'ادامه سفارش',
    orderType_label:   'سفارش خود را چگونه دریافت می‌کنید؟',
    tableNum:          'شماره میز',
    deliveryAddr:      'آدرس تحویل',
    specialInstructions:'توضیحات خاص',
    paymentMethod_label:'روش پرداخت',
    choosePayment:     'روش پرداخت را انتخاب کنید...',
    total_label:       'مجموع',
    items_label:       'مورد',
    removeItem:        'حذف',
    loginToContinue:   'برای ثبت سفارش وارد شوید',

    /* ── احراز هویت مشتری ── */
    welcomeBack:       'خوش آمدید',
    createAccount:     'ایجاد حساب کاربری',
    loginToContinue2:  'برای ادامه وارد شوید',
    dontHaveAccount:   'حساب کاربری ندارید؟',
    alreadyHaveAccount:'قبلاً ثبت‌نام کرده‌اید؟',
    registerHere:      'اینجا ثبت‌نام کنید',
    loginHere:         'اینجا وارد شوید',
    fullName:          'نام و نام خانوادگی',
    confirmPassword:   'تأیید رمز عبور',
    forgotPassword:    'رمز عبور را فراموش کرده‌اید؟',
    orContinueAs:      'یا ادامه به عنوان مهمان',
    signInBtn:         'ورود',
    registerBtn:       'ایجاد حساب',
    loggingIn:         'در حال ورود...',
    registering:       'در حال ایجاد حساب...',

    /* ── پروفایل مشتری ── */
    myProfile:         'پروفایل من',
    myOrders:          'سفارش‌های من',
    editProfile:       'ویرایش پروفایل',
    saveProfile:       'ذخیره پروفایل',
    profileSaved:      'پروفایل ذخیره شد ✓',
    noOrdersYet:       'هنوز سفارشی ثبت نشده است.',
    orderDate:         'تاریخ',
    orderItems:        'اقلام',
    orderTotal:        'مبلغ',

    /* ── داشبورد کسب‌وکار ── */
    businessDashboard: 'داشبورد کسب‌وکار',
    myRestaurants:     'رستوران‌های من',
    manageRestaurant:  'مدیریت رستوران',
    openAdminPanel:    'باز کردن پنل مدیریت',
    businessLogin:     'ورود صاحب کسب‌وکار',
    businessRegister:  'ایجاد حساب کسب‌وکار',
    noRestLinked:      'هیچ رستورانی به حساب شما متصل نشده است.',
    contactSupport:    'برای افزودن رستوران با پشتیبانی تماس بگیرید.',

    /* ── ورود پلتفرم ── */
    platformLogin:     'ورود مدیر پلتفرم',
    platformLoginSub:  'دسترسی محدود — فقط برای پرسنل مجاز',
    platformSignIn:    'ورود به پلتفرم',

    /* ── ورود مدیر رستوران ── */
    adminLogin:        'ورود مدیر',
    backToSite:        '→ بازگشت به سایت',
    businessOwnerLogin:'ورود صاحب کسب‌وکار',
    demo:              'نمونه',

    /* ── ثبت‌نام / ورود کسب‌وکار ── */
    bizTagline:          'پلتفرم جامع منوی دیجیتال برای رستوران‌ها و کافه‌ها',
    bizFeatureMenu:      'منوی زیبا با زیردامنه اختصاصی',
    bizFeatureOrders:    'مدیریت سفارش‌ها به‌صورت لحظه‌ای',
    bizFeatureProducts:  'مدیریت محصولات و دسته‌بندی‌ها',
    bizFeatureInsights:  'تحلیل و بینش مشتریان',
    bizFeatureTenant:    'چند‌مستأجری — کاملاً مجزا',
    bizFeatureMobile:    'تجربه موبایل‌محور',
    bizRegisterBusiness: 'ثبت کسب‌وکار',
    bizSignInDashboard:  'ورود به داشبورد',
    bizRegistering:      'در حال ثبت‌نام...',
    bizRegisterMyBiz:    'ثبت کسب‌وکار من 🚀',
    bizBusinessEmail:    'ایمیل کسب‌وکار',
    bizRestaurantName:   'نام رستوران',
    bizOwnerName:        'نام صاحب کسب‌وکار',
    bizMenuSubdomain:    'زیردامنه منو',
    bizUsedForSms:       'برای ورود پیامکی',
    bizCreateStrongPass: 'یک رمز عبور قوی بسازید',
    bizTerms:            'با ثبت‌نام، شرایط استفاده و سیاست حریم خصوصی ما را می‌پذیرید.',
    bizBackToHome:       '→ بازگشت به خانه',
    bizPhoneMethod:      '📱 تلفن',
    bizEmailMethod:      '✉️ ایمیل',
    bizEmailReq:         'ایمیل و رمز عبور الزامی است.',
    bizAllFieldsReq:     'لطفاً همهٔ فیلدهای الزامی را پر کنید.',

    /* ── ویجت ورود با کد پیامکی (به PhoneOtpForm پاس داده می‌شود) ── */
    otpPhoneLabel:       'شماره موبایل',
    otpSendBtn:          'ارسال کد',
    otpCodeLabel:        'کد تأیید',
    otpVerifyBtn:        'تأیید و ادامه',
    otpNameLabel:        'نام شما (اختیاری)',
    otpNamePh:           'مثلاً سارا',
    otpChangePhone:      'تغییر شماره',
    otpResend:           'ارسال مجدد کد',
    otpSentTo:           'کد به این شماره ارسال شد',

    /* ── زبان پلتفرم + برچسب‌های فرم ── */
    languageMode:          'حالت زبان',
    defaultLanguage:       'زبان پیش‌فرض',
    enabledLanguages:      'زبان‌های فعال',
    restaurantName:        'نام رستوران',
    platformUiLangMode:    'حالت زبان رابط پلتفرم',
    defaultPlatformLang:   'زبان پیش‌فرض پلتفرم',
    enabledLangsUiSwitcher:'زبان‌های فعال برای تغییردهنده زبان',
    enabledLangsHint:      'این زبان‌ها در منوی پرچم‌ها در صفحه اصلی پلتفرم نمایش داده می‌شوند.',
    switcherPreview:       'پیش‌نمایش تغییردهنده زبان',
    saveLanguageSettings:  'ذخیره تنظیمات زبان',
    dashboardLanguage:     'زبان داشبورد',
    dashboardLanguageHint: 'زبانی که شما و سایر مدیران این داشبورد و صفحات ورود را با آن می‌بینید. مستقل از زبان سایت عمومی در پایین است.',
    publicSiteLanguage:    'زبان سایت عمومی',
    publicSiteLanguageHint:'زبان(هایی) که بازدیدکنندگان در صفحه اصلی عمومی و پوسته می‌بینند.',
    polishTitle:           'کیفیت ترجمه (بهبود با هوش مصنوعی)',
    polishHint:            'یک زبان ترجمه‌شده خودکار را دوباره بررسی می‌کند و عبارت‌های نامناسب یا تحت‌اللفظی را بازنویسی می‌کند. ابتدا از هوش مصنوعی محلی شما و سپس از ابر استفاده می‌کند.',
    polishBtn:             '✨ بهبود با هوش مصنوعی',
    polishing:             'در حال بهبود…',
    polishDoneMsg:         'بهبود یافت — {n} رشته اصلاح شد ✓',
    polishNoneMsg:         'از قبل خوب است — تغییری لازم نبود ✓',
    platformHomepageTranslations: 'ترجمه‌های صفحه اصلی پلتفرم',
    translateInto:         'ترجمه به',
    adminPanelLanguage:    'زبان پنل مدیریت',
    adminPanelLangDesc:    'فقط برچسب‌ها، تب‌ها و دکمه‌های داخل این داشبورد را ترجمه می‌کند. مشتریان شما هرگز این متن‌ها را نمی‌بینند. انگلیسی همیشه زبان جایگزین است.',
    chooseLanguage:        'انتخاب زبان',

    /* ── پیام‌های پلتفرم ── */
    tAdminCreated:     'مدیر ایجاد شد ✓',
    tAdminDeleted:     'مدیر حذف شد',
    tAdminUpdated:     'مدیر به‌روزرسانی شد ✓',
    tAdminTranslated:  'پنل مدیریت ترجمه شد ✓',
    tDashLangUpdated:  'زبان داشبورد به‌روزرسانی شد ✓',
    tPlanUpdated:      'پلن به‌روزرسانی شد',
    tSettingsSaved:    'تنظیمات پلتفرم ذخیره شد ✓',
    tTransDone:        'ترجمه‌های پلتفرم انجام شد ✓',
    tResetDefaults:    'بازنشانی به انگلیسی ✓',
    tRestDeleted:      'رستوران حذف شد',
    tRestUpdated:      'رستوران به‌روزرسانی شد ✓',
    tLangFailed:       'تغییر زبان ممکن نشد',
    tRestDeactivated:  'رستوران غیرفعال شد',
    tRestActivated:    'رستوران فعال شد',
    tOwnerDeactivated: 'صاحب کسب‌وکار غیرفعال شد',
    tOwnerActivated:   'صاحب کسب‌وکار فعال شد',

    /* ── حالت سیستم ── */
    systemMode:        'حالت سیستم',
    accentColor:       'رنگ ثانویه',
    baseMode:          'حالت پایه',
    dark:              'تیره',
    light:             'روشن',
    production:        'عملیاتی (Production)',
    development:       'توسعه (Development)',
    systemModeHint:    'حالت عملیاتی پیامک واقعی ارسال می‌کند. حالت توسعه کد را در کنسول سرور ثبت کرده و روی صفحه نشان می‌دهد (بدون ارسال پیامک و بدون مصرف اعتبار).',
    resetPassword:     'بازنشانی رمز عبور',
    resetPasswordSub:  'شماره تلفن حساب خود و یک رمز عبور جدید را وارد کنید. برای تأیید یک کد ارسال می‌کنیم.',
    forgotPassword:    'رمز عبور را فراموش کرده‌اید؟',

    /* ── پنل آمار ── */
    analyticsPeriod:   'بازه زمانی',
    days7:             '۷ روز',
    days14:            '۱۴ روز',
    days30:            '۳۰ روز',
    days90:            '۹۰ روز',
    revenue:           'درآمد',
    ordersCount:       'سفارش‌ها',
    avgOrder:          'میانگین سفارش',
    activeRests:       'رستوران‌های فعال',
    todayRevenue:      'درآمد امروز',
    todayOrders2:      'سفارش‌های امروز',
    topProducts:       'پرفروش‌ترین محصولات',
    topRestaurants:    'برترین رستوران‌ها',
    recentActivity:    'فعالیت‌های اخیر',
    auditLogs:         'گزارش رویدادها',
    aiAnalyst:         'تحلیل‌گر هوش مصنوعی',
    exportCSV:         '⬇ خروجی CSV',
    noLogs:            'گزارشی پیدا نشد.',
    askAI:             'درباره داده‌های خود بپرسید...',
    send:              'ارسال',
    aiThinking:        'در حال تحلیل داده‌ها...',
    aiSuggestions:     ['پرفروش‌ترین محصولات این ماه کدامند؟', 'درآمد این ماه نسبت به ماه قبل چطور است؟', 'ساعات اوج فروش من چه زمانی است؟', 'چه محصولاتی را باید تبلیغ کنم؟'],
    tabOverview:       'نمای کلی',
    tabProducts:       'محصولات',
    tabActivity:       'فعالیت‌ها',
    tabAI:             'هوش مصنوعی',
    tabAudit:          'گزارش رویدادها',

    /* ── اعلان‌ها ── */
    savedOK:           'ذخیره شد ✓',
    deletedOK:         'حذف شد',
    updatedOK:         'بروزرسانی شد ✓',
    createdOK:         'ایجاد شد ✓',
    errorOccurred:     'خطایی رخ داده است',
    confirmDelete:     'آیا مطمئن هستید که می‌خواهید این مورد را حذف کنید؟',

    /* ── خطاها ── */
    serverError:       'امکان اتصال به سرور وجود ندارد',
    serverErrorDesc:   'لطفاً اتصال خود را بررسی کرده و دوباره تلاش کنید.',
    pageNotFound:      'صفحه پیدا نشد',
    pageNotFoundDesc:  'صفحه‌ای که دنبال آن هستید وجود ندارد.',
    maintenance:       'در حال تعمیر و نگهداری',
    maintenanceDesc:   'در حال حاضر در حال انجام تعمیرات هستیم. لطفاً به زودی مجدداً مراجعه کنید.',
  },
};

export default STRINGS;
